import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Modal } from '@unified-checkout/design-system';
import type { IButton } from 'libs/design-system/src/types/Button/button';
import type { IMerchantStatement } from '@app/dashboard/types/innerLayout/services/merchant-statement';
import { environment } from '@app/dashboard/environments/environment';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { MAX_NOTIFICATION_WIDTH, NOTIFICATION_TIMEOUT } from '@app/dashboard/constants/transactions';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { changeSubscription } from '@app/dashboard/services/inner_layout/services/change-subscription';
import { getServices } from '@app/dashboard/services/inner_layout/services/get-services';
import { notify } from '@app/dashboard/utils/notifier';
import { addToLocalStorage, getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';

export const MerchantStatementModal = ({
    isMerchantStatementsOpen,
    setisMerchantStatementsOpen,
    isMerchantStatementEnabled,
    setIsMerchantStatementEnabled
}: IMerchantStatement) => {
    const { t } = useTranslation();

    const merchantStatementId = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS)?.id;

    useEffect(() => {
        if (isMerchantStatementEnabled === null || (isMerchantStatementEnabled && merchantStatementId === null)) {
            mutateServices();
        }
    }, [isMerchantStatementEnabled]);

    const {
        mutate: mutateServices,
    } = useMutation({
        mutationKey: [QUERY_KEYS.Get_SERVICES],
        mutationFn: () => getServices(),
        onSuccess: (resp) => {
            if (resp?.data?.results && resp.data.results.length > 0) {
                const merchantStatementService = resp.data.results.find((service: any) =>
                    service.service_name === environment.STATEMENTS_SERVICE_NAME ||
                    service.service_name === environment.STATEMENTS_SERVICE_NAME_ALPHA
                );
                if (merchantStatementService) {
                    addToLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS, {
                        enabled: merchantStatementService.active,
                        id: merchantStatementService.service_id
                    });
                } else {
                    addToLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS, null);
                }
            } else {
                addToLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS, null);
            }
        },
        onError: () => {
            addToLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS, null);
        }
    });

    const {
        mutate: mutateChangeSubscription,
        isPending,
    } = useMutation({
        mutationKey: [QUERY_KEYS.CHANGE_SUBSCRIPTION],
        mutationFn: (isSubscribeAction: boolean) => {
            return changeSubscription(merchantStatementId, isSubscribeAction);
        },
        onSuccess: (_, isSubscribeAction) => {
            addToLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS, {
                enabled: isSubscribeAction,
                id: merchantStatementId
            });
            setIsMerchantStatementEnabled(isSubscribeAction);
            const successMessage = isSubscribeAction ?
                t('innerLayout.servicesPage.modals.merchantStatements.subscribeSuccess')
                :
                t('innerLayout.servicesPage.modals.merchantStatements.unsubscribeSuccess');
            notify.success(successMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT });
            setisMerchantStatementsOpen(false);
        },
        onError: (error: any) => {
            const errMessage = error.response?.data?.message ?? t('innerLayout.servicesPage.modals.merchantStatements.failed');
            notify.error(errMessage, { maxWidth: MAX_NOTIFICATION_WIDTH, timeout: NOTIFICATION_TIMEOUT });
            setisMerchantStatementsOpen(false);
        }
    })

    const handleToggleSubscription = (isSubscribeAction: boolean) => {
        mutateChangeSubscription(isSubscribeAction);
    };

    const cancelBtnProps: IButton = {
        text: t('innerLayout.servicesPage.modals.btns.cancel'),
        size: "lg",
        variant: "outlined",
        color: "secondary",
        disabled: isPending,
        onClickHandler: () => setisMerchantStatementsOpen(false)
    };

    const actionButton: IButton = isMerchantStatementEnabled ?
        {
            text: t('innerLayout.servicesPage.modals.btns.unsubscrbe'),
            size: "lg",
            variant: "contained",
            color: "danger",
            loading: isPending,
            disabled: isPending,
            onClickHandler: () => handleToggleSubscription(false)
        }
        :
        {
            text: t('innerLayout.servicesPage.modals.btns.subscribe'),
            size: "lg",
            variant: "contained",
            color: "primary",
            loading: isPending,
            disabled: isPending,
            onClickHandler: () => handleToggleSubscription(true)
        };

    const title = isMerchantStatementEnabled ?
        t('innerLayout.servicesPage.modals.merchantStatements.unsubscribeTitle')
        :
        t('innerLayout.servicesPage.modals.merchantStatements.subscribeTitle');

    const description = isMerchantStatementEnabled ?
        t('innerLayout.servicesPage.modals.merchantStatements.unsubscribeDesc')
        :
        t('innerLayout.servicesPage.modals.merchantStatements.subscribeDesc');

    const btns = [actionButton, cancelBtnProps];

    return (
        <>
            <Modal
                state="default"
                open={isMerchantStatementsOpen}
                position="center"
                onCloseHandler={() => setisMerchantStatementsOpen(false)}
                title={title}
                supportingText={description}
                btnsProps={btns}
            />
        </>
    );
}

